/**
 * 「现在正在拍照」这件事必须写在磁盘上（CR-037）。
 *
 * 安卓在相机 App 前台运行时**可以随时回收我们这个 Activity** —— 相机是内存大户，
 * 工地手机内存又普遍紧张。回收之后拍完回来，WebView 是**重新加载**的：
 * React 从头挂载，路由回到首页，表单里填的字一个不剩。
 * 客户原话：「填写的内容在拍照和视频录取后丢失，需要重新填写」。
 *
 * 所以拍照前要在内存之外留三样东西：
 * - `owner`：这次拍照是替哪份草稿拍的，照片恢复回来要放回它；
 * - `route`：拍照前停在哪一页，重建之后要回到这里；
 * - `preset`：用哪一档压缩，恢复出来的原图要按同一档处理。
 *
 * **用 localStorage 而不是内存**：这条记录唯一的用处就是挺过进程被杀，
 * 存在内存里等于没存。
 *
 * 照片本身由 Capacitor 负责送回来（`appRestoredResult`），这里只管「是谁在拍」。
 * 视频走的是 `<input type=file>`，没有插件回调，**录到一半被杀就是丢了** ——
 * 但草稿里的文字仍然能恢复，而且能回到那一页。
 */

const KEY = 'capture:inflight';

/** 超过这个时间还留着的记录当作垃圾。正常拍一张照不会超过几分钟。 */
export const CAPTURE_MAX_AGE_MS = 15 * 60_000;

export type CapturePresetName = 'normal' | 'archive';

export interface CaptureSession {
  /** 草稿 key，照片恢复回来放回这份草稿 */
  owner: string;
  /** 拍照前所在的路由 */
  route: string;
  preset: CapturePresetName;
  /** 调起相机的时刻。恢复出来的照片没有拍摄时间，用它兜底 */
  startedAt: number;
}

/**
 * 解析存下来的记录。**纯函数，单独拿出来是为了能测**：
 * 时间边界和脏数据这两种情况在真机上都不好复现。
 */
export function parseSession(
  raw: string | null,
  now: number,
  maxAgeMs = CAPTURE_MAX_AGE_MS,
): CaptureSession | null {
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const value = parsed as Partial<CaptureSession>;
  if (typeof value.owner !== 'string' || !value.owner) return null;
  if (typeof value.route !== 'string' || !value.route) return null;
  if (value.preset !== 'normal' && value.preset !== 'archive') return null;
  if (typeof value.startedAt !== 'number' || !Number.isFinite(value.startedAt)) return null;

  // 太老的记录不认：工人可能几小时前取消过一次拍照，那时留下的路由
  // 不该在今天冷启动时把他甩到隐蔽工程页
  if (now - value.startedAt > maxAgeMs) return null;
  // 时间在未来说明设备时钟被改过，同样不认
  if (value.startedAt > now) return null;

  return value as CaptureSession;
}

export function beginCapture(session: Omit<CaptureSession, 'startedAt'>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...session, startedAt: Date.now() }));
  } catch {
    // 存不下就退化成「没有恢复能力」，不能因此挡住拍照
  }
}

export function endCapture(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* 同上 */
  }
}

/** 读出还有效的那条记录。读不到或已过期返回 null。 */
export function readCapture(): CaptureSession | null {
  try {
    return parseSession(localStorage.getItem(KEY), Date.now());
  } catch {
    return null;
  }
}
